import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, CheckCircle2, AlertTriangle, AlertOctagon, Ban, Loader2, Camera, Phone, Mic, Check, Package } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtEUR, EVENT_TYPES } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TONES = {
  green: { wrap: "border-emerald-300 bg-emerald-50", text: "text-emerald-700" },
  amber: { wrap: "border-amber-300 bg-amber-50", text: "text-amber-800" },
  red: { wrap: "border-rose-300 bg-rose-50", text: "text-rose-700" },
};

const VERDICTS = {
  DISPONIBLE: { big: "OUI — DISPONIBLE", tone: "green", Icon: CheckCircle2 },
  DISPONIBLE_3E: { big: "OUI — 3e PHOTOBOOTH POSSIBLE", tone: "green", Icon: CheckCircle2 },
  TRES_FAVORABLE: { big: "OUI — TRÈS FAVORABLE", tone: "green", Icon: CheckCircle2 },
  FAIBLE_CONTRAINTE: { big: "OUI — FAIBLE CONTRAINTE SUPPLÉMENTAIRE", tone: "green", Icon: CheckCircle2 },
  A_ETUDIER: { big: "À ÉTUDIER", tone: "amber", Icon: AlertTriangle },
  FAISABLE_AVEC_ORGANISATION: { big: "FAISABLE AVEC ORGANISATION", tone: "amber", Icon: AlertTriangle },
  HORS_ZONE: { big: "HORS ZONE HABITUELLE", tone: "amber", Icon: AlertTriangle },
  VILLE_INCONNUE: { big: "À ÉTUDIER", tone: "amber", Icon: AlertTriangle },
  NON_RECOMMANDE: { big: "NON RECOMMANDÉ", tone: "red", Icon: AlertOctagon },
  COMPLET: { big: "COMPLET", tone: "red", Icon: Ban },
  INDISPONIBLE: { big: "INDISPONIBLE À CETTE DATE", tone: "red", Icon: Ban },
};

const KINDS = [
  ["photobooth", "Photobooth", Camera],
  ["agb", "Audio Guest Book", Phone],
  ["cabine", "Cabine Audio Guest Book", Mic],
];

const KIND_META = Object.fromEntries(KINDS.map(([k, label, Icon]) => [k, { label, Icon }]));

const GLOBALS = {
  all: { label: "ENSEMBLE DISPONIBLE", tone: "green", Icon: CheckCircle2 },
  partial: { label: "DISPONIBILITÉ PARTIELLE", tone: "amber", Icon: AlertTriangle },
  none: { label: "AUCUN MATÉRIEL DISPONIBLE", tone: "red", Icon: Ban },
};

const STATUS_TXT = {
  ok: { label: "Disponible", cls: "text-emerald-700", dot: "bg-emerald-500" },
  study: { label: "À étudier", cls: "text-amber-700", dot: "bg-amber-500" },
  no: { label: "Indisponible", cls: "text-rose-700", dot: "bg-rose-500" },
};

function WeekendContext({ result }) {
  const assigned = result.events.filter((e) => e.booth_id !== null && e.booth_id !== undefined);
  const unassigned = result.events.filter((e) => e.booth_id === null || e.booth_id === undefined);
  return (
    <div data-testid="quick-check-weekend-context" className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Déjà prévu sur cette période</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => {
          const ev = assigned.find((e) => e.booth_id === i);
          const trip = (result.existing_trips || []).find((t) => t.booth_id === i);
          return (
            <div key={i} data-testid={`quick-check-slot-${i}`} className="rounded-lg bg-white border border-slate-200 p-2.5">
              <p className="text-xs font-bold text-slate-700">{result.booth_names[i]}</p>
              {ev ? (
                <>
                  <p className="text-sm font-medium mt-0.5">{ev.type} — {ev.city}</p>
                  <p className="text-xs text-slate-500">{fmtDate(ev.date)}{ev.client ? ` · ${ev.client}` : ""}</p>
                  {trip && trip.duration_min !== null && trip.duration_min !== undefined && (
                    <p className="text-xs font-medium text-sky-700" data-testid={`quick-check-slot-${i}-duration`}>{trip.duration_min} min de Lesménils</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-emerald-600 font-semibold mt-0.5">Disponible</p>
              )}
            </div>
          );
        })}
      </div>
      {unassigned.map((e, idx) => (
        <p key={idx} className="text-xs text-slate-500">+ {e.type} — {e.city} ({fmtDate(e.date)}) · machine non attribuée</p>
      ))}
    </div>
  );
}

function TripsDetail({ result }) {
  if (!result.trips?.length) return null;
  return (
    <div data-testid="quick-check-trips" className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
        Détail des trajets (interne){result.deliveries_frisat ? ` — ${result.deliveries_frisat} livraison(s) vendredi + samedi` : ""}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {result.trips.map((t, i) => (
          <div key={i} data-testid={`quick-check-trip-${i}`} className={`rounded-lg border p-2.5 ${t.is_new ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
            <p className="text-xs font-bold text-slate-700">
              {t.is_new ? "NOUVELLE DEMANDE" : (result.booth_names?.[t.booth_id] || `Photobooth ${(t.booth_id ?? 0) + 1}`)}
            </p>
            <p className="text-sm font-medium mt-0.5">{t.city}{t.client ? ` · ${t.client}` : ""}</p>
            {t.duration_min !== null && t.duration_min !== undefined && (
              <p className="text-xs font-mono tabular-nums text-slate-600">{t.dist_km} km · {t.duration_min} min de Lesménils</p>
            )}
            {t.delivery_label && <p className="text-xs text-slate-500">Livraison : {t.delivery_label}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function QuickCheck() {
  const navigate = useNavigate();
  const [kinds, setKinds] = useState(["photobooth"]);
  const [date, setDate] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState(null);
  const [eventType, setEventType] = useState("Mariage");
  const [formula, setFormula] = useState("200");
  const [formulas, setFormulas] = useState([]);
  useEffect(() => { api.get("/settings").then((r) => setFormulas(r.data.formulas || [])).catch(() => {}); }, []);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("fin-apres-midi");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stephaneDone, setStephaneDone] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pb54_checks") || "[]"); } catch { return []; }
  });

  const isBooth = kinds.includes("photobooth");
  const pb = result?.results?.photobooth || null;
  const cfg = pb ? VERDICTS[pb.verdict] : null;
  const tone = cfg ? TONES[cfg.tone] : null;
  const g = result ? GLOBALS[result.global] : null;
  const gtone = g ? TONES[g.tone] : null;

  const toggleKind = (k) => {
    setResult(null);
    setKinds((ks) => {
      const next = ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k];
      return next.length ? next : ks;
    });
  };

  const onCityChange = (v) => {
    setCity(v);
    setPostcode(null);
    setShowSug(true);
    if (v.trim().length >= 2) {
      api.get("/cities", { params: { q: v } }).then((r) => setSuggestions(r.data)).catch(() => {});
    } else {
      setSuggestions([]);
    }
  };

  const check = async () => {
    if (!date) { toast.error("Saisissez la date de l'événement"); return; }
    if (isBooth && !city.trim()) { toast.error("Saisissez une ville"); return; }
    setLoading(true);
    try {
      const r = await api.post("/logistics/multi-check", { kinds, date, city: city.trim(), postcode, event_type: eventType, formula, delivery_date: deliveryDate || null, delivery_slot: deliverySlot || null });
      setResult(r.data);
      setStephaneDone(false);
      if (r.data.results?.photobooth) {
        const entry = { date, city: city.trim(), verdict: r.data.results.photobooth.verdict };
        const h = [entry, ...history.filter((x) => !(x.date === entry.date && x.city.toLowerCase() === entry.city.toLowerCase()))].slice(0, 6);
        setHistory(h);
        localStorage.setItem("pb54_checks", JSON.stringify(h));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vérification impossible");
    } finally {
      setLoading(false);
    }
  };

  const createBooking = () => {
    navigate("/reservations/nouvelle", {
      state: {
        kinds, kind: kinds[0], date, city: city.trim(), postcode, formula,
        verdict: pb?.verdict, delivery_fee: pb?.supplement || 0, event_type: eventType,
        availability: result?.status || {},
      },
    });
  };
  const dismiss = () => setResult(null);

  const planStephane = async () => {
    const sp = pb?.stephane_proposal;
    if (!sp?.reservation_id) return;
    try {
      await api.post(`/reservations/${sp.reservation_id}/stephane`, { cost: sp.cost });
      setStephaneDone(true);
      toast.success("Livraison confiée à Stéphane — coût intégré au bénéfice de la réservation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const partialDetail = result
    ? result.kinds.map((k) => `${KIND_META[k].label} ${STATUS_TXT[result.status[k]].label.toLowerCase()}`).join(" — ")
    : "";
  const eqCreatable = result && !pb && result.kinds.some((k) => result.status[k] === "ok");

  return (
    <section data-testid="quick-check" className="rounded-2xl border-2 border-sky-200 bg-white p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-sky-500 flex items-center justify-center shrink-0">
          <Zap className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Une demande ? Vérifier rapidement</h2>
          <p className="text-xs text-slate-500">Un ou plusieurs matériels en une seule recherche — sans saisir le client.</p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Matériel demandé (un ou plusieurs)</p>
        <div className="grid grid-cols-3 gap-2" data-testid="quick-check-kind-selector">
          {KINDS.map(([k, label, Icon]) => {
            const active = kinds.includes(k);
            return (
              <button
                key={k}
                type="button"
                data-testid={`quick-check-kind-${k}`}
                aria-pressed={active}
                onClick={() => toggleKind(k)}
                className={`relative flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3.5 text-center transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-sky-400"}`}
              >
                <span className={`absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded border transition-colors ${active ? "border-white bg-white text-slate-900" : "border-slate-300 text-transparent"}`}>
                  <Check className="h-3 w-3" />
                </span>
                <Icon className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-bold leading-tight">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date de l'événement</label>
          <Input data-testid="quick-check-date-input" type="date" min={new Date().toISOString().slice(0, 10)} value={date} onChange={(e) => setDate(e.target.value)} />
          {date && <p data-testid="quick-check-date-day" className="text-xs font-semibold text-sky-700">{fmtDate(date)}</p>}
        </div>
        {isBooth && (
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type d'événement</label>
            <select data-testid="quick-check-type-select" value={eventType} onChange={(e) => setEventType(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        {isBooth && (
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Formule photobooth</label>
            <select data-testid="quick-check-formula-select" value={formula} onChange={(e) => setFormula(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              {formulas.filter((f) => f.id !== "autre").map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              {formulas.length === 0 && <option value="200">200 photos</option>}
            </select>
          </div>
        )}
        {isBooth && !["Mariage", "Anniversaire"].includes(eventType) && (
          <>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date de livraison</label>
              <Input data-testid="quick-check-delivery-date" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Créneau</label>
              <select data-testid="quick-check-delivery-slot" value={deliverySlot} onChange={(e) => setDeliverySlot(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="matin">Matin</option>
                <option value="apres-midi">Après-midi</option>
                <option value="fin-apres-midi">Fin d'après-midi</option>
              </select>
            </div>
          </>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        {isBooth ? (
          <div className="space-y-1 relative">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ville / Code postal</label>
            <Input
              data-testid="quick-check-city-input"
              value={city}
              onChange={(e) => onCityChange(e.target.value)}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              placeholder="Ex. Metz, Nancy…"
              autoComplete="off"
            />
            {showSug && suggestions.length > 0 && (
              <ul data-testid="quick-check-city-suggestions" className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-auto">
                {suggestions.map((s) => (
                  <li key={s.label}>
                    <button
                      type="button"
                      data-testid={`quick-check-city-option-${s.name}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 transition-colors"
                      onMouseDown={() => { setCity(s.name); setPostcode(s.postcode || null); setSuggestions([]); setShowSug(false); }}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500 self-center" data-testid="quick-check-equipment-note">
            Retrait et retour directement à Lesménils — aucune livraison, aucun supplément kilométrique.
          </p>
        )}
        <div className="flex items-end">
          <Button data-testid="quick-check-submit" onClick={check} disabled={loading} className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-semibold h-10 transition-colors">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Vérifier
          </Button>
        </div>
      </div>

      {result && g && gtone && (
        <div data-testid="quick-check-result" className={`rounded-xl border p-4 space-y-3 ${gtone.wrap}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Résultat</p>
          <ul className="space-y-1.5">
            {result.kinds.map((k) => {
              const { label, Icon } = KIND_META[k];
              const st = STATUS_TXT[result.status[k]];
              const eq = k !== "photobooth" ? result.results[k] : null;
              return (
                <li key={k} data-testid={`quick-check-line-${k}`} className="flex items-start gap-2">
                  <Icon className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
                  <div className="text-sm min-w-0">
                    <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${st.dot}`} />
                    <span className="font-semibold text-slate-800">{label}</span>
                    <span className={`ml-2 font-bold ${st.cls}`} data-testid={`quick-check-line-${k}-status`}>{st.label}</span>
                    {k === "photobooth" && pb && <span className="block text-xs text-slate-500">{VERDICTS[pb.verdict]?.big}</span>}
                    {eq && result.status[k] === "ok" && (
                      <span className="block text-xs text-slate-500">
                        {fmtEUR(eq.price)} — retrait et retour à Lesménils{eq.remaining !== null && eq.remaining !== undefined ? ` · stock restant : ${eq.remaining}` : ""}
                      </span>
                    )}
                    {eq?.relais_note && <span className="block text-xs font-medium text-amber-700" data-testid={`quick-check-line-${k}-relais`}>{eq.relais_note}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
          <p data-testid="quick-check-global-verdict" className={`font-heading text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2 ${gtone.text}`}>
            <g.Icon className="h-6 w-6 shrink-0" /> {g.label}
          </p>
          {result.global === "partial" && <p data-testid="quick-check-partial-detail" className="text-sm text-slate-700">{partialDetail}</p>}
          {result.global === "partial" && pb && result.status.photobooth === "no" && result.kinds.some((k) => k !== "photobooth" && result.status[k] === "ok") && (
            <p data-testid="quick-check-retrait-fallback" className="text-sm font-medium text-slate-700">
              Le photobooth n'est pas disponible dans ces conditions, mais le matériel audio reste disponible en retrait à Lesménils.
            </p>
          )}
          {eqCreatable && (
            <Button data-testid="quick-check-create-booking" onClick={createBooking} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors">
              Créer la réservation
            </Button>
          )}
        </div>
      )}

      {pb && cfg && tone && (
        <div data-testid="quick-check-booth-detail" className={`rounded-xl border p-4 space-y-3 ${tone.wrap}`}>
          <p data-testid="quick-check-verdict" className={`font-heading text-lg sm:text-xl font-extrabold tracking-tight flex items-center gap-2 ${tone.text}`}>
            <cfg.Icon className="h-6 w-6 shrink-0" /> Photobooth : {cfg.big}
          </p>
          <p className="text-sm text-slate-700">{pb.message}</p>
          {pb.charge && (
            <p data-testid="quick-check-charge" className="text-sm font-semibold text-slate-700">
              Charge logistique actuelle : {pb.charge}
            </p>
          )}
          {pb.dist_km !== null && pb.dist_km !== undefined && (
            <p className="text-sm font-medium font-mono tabular-nums text-slate-700">Depuis Lesménils : {pb.dist_km} km / {pb.duration_min} min</p>
          )}
          {pb.supplement !== undefined && pb.dist_km !== null && pb.dist_km !== undefined && (
            <div data-testid="quick-check-supplement" className="text-sm text-slate-700 space-y-0.5">
              {pb.supplement > 0 ? (
                <>
                  <p>Livraison + reprise incluses sur les {pb.km_inclus} premiers km</p>
                  <p>Kilomètres supplémentaires facturés : {pb.billed_km} km</p>
                  <p className="font-bold font-mono tabular-nums">Supplément déplacement : {fmtEUR(pb.supplement)}</p>
                </>
              ) : (
                <p className="font-semibold">Livraison et reprise incluses : 0 €</p>
              )}
            </div>
          )}
          {pb.delivery_estimate && (
            <p data-testid="quick-check-delivery-estimate" className="text-sm text-slate-600">Livraison probable : <strong>{pb.delivery_estimate.label}</strong></p>
          )}
          {result.results?.consumable && (
            <p data-testid="quick-check-consumable" className={`text-sm font-medium flex items-center gap-1.5 ${result.results.consumable.ok ? "text-emerald-700" : "text-amber-700"}`}>
              <Package className="h-4 w-4 shrink-0" /> {result.results.consumable.ok ? "🟢" : "🟠"} Consommable (formule {result.results.consumable.formula}) : {result.results.consumable.label}
            </p>
          )}
          {pb.relais_note && (
            <p data-testid="quick-check-relais-note" className="text-sm font-medium text-amber-700">{pb.relais_note}</p>
          )}
          {pb.cabine_available !== undefined && !kinds.includes("cabine") && !kinds.includes("agb") && (
            <div className="text-xs text-slate-600 space-y-0.5" data-testid="quick-check-equipment">
              <p>Cabine Audio Guest Book : {pb.cabine_available ? `disponible — ${fmtEUR(pb.cabine_price)} (à retirer à Lesménils)` : "indisponible"}</p>
              {pb.agb_remaining !== undefined && <p>Audio Guest Book offert : {pb.agb_remaining > 0 ? "disponible" : "indisponible"}</p>}
              {pb.formula_warning && <p className="font-medium text-amber-700">{pb.formula_warning}</p>}
            </div>
          )}
          {pb.extra_min !== undefined && (
            <p className="text-sm font-medium font-mono tabular-nums text-slate-700">Détour supplémentaire : {pb.extra_min} min / {pb.extra_km} km</p>
          )}
          {pb.stephane_proposal && !stephaneDone && (
            <div data-testid="quick-check-stephane" className="rounded-xl border border-amber-300 bg-amber-100/70 p-3 space-y-2">
              <p className="text-sm font-semibold text-amber-900">Possibilité de confier une livraison à Stéphane</p>
              <p className="text-xs text-amber-800">
                {pb.stephane_proposal.city} ({pb.stephane_proposal.client}) — {pb.stephane_proposal.dist_km} km · {pb.stephane_proposal.duration_min} min de Lesménils
                — livraison {pb.stephane_proposal.delivery_day} — coût interne {fmtEUR(pb.stephane_proposal.cost)}
              </p>
              <Button size="sm" data-testid="quick-check-stephane-plan" onClick={planStephane} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">
                Prévoir Stéphane
              </Button>
            </div>
          )}
          {stephaneDone && (
            <p data-testid="quick-check-stephane-done" className="text-sm font-semibold text-emerald-700">✓ Livraison confiée à Stéphane — coût intégré au bénéfice de la réservation.</p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {cfg.tone === "green" && (
              <Button data-testid="quick-check-create-booking-booth" onClick={createBooking} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors">
                Créer la réservation
              </Button>
            )}
            {cfg.tone === "amber" && (
              <>
                <Button data-testid="quick-check-accept" onClick={createBooking} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold transition-colors">
                  Accepter quand même
                </Button>
                <Button data-testid="quick-check-refuse" variant="outline" onClick={dismiss}>Refuser</Button>
              </>
            )}
            {cfg.tone === "red" && (
              <>
                <Button data-testid="quick-check-accept-exceptional" onClick={createBooking} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100">
                  Accepter exceptionnellement
                </Button>
                <Button data-testid="quick-check-close" variant="ghost" onClick={dismiss}>Fermer</Button>
              </>
            )}
          </div>
        </div>
      )}

      {pb && <TripsDetail result={pb} />}

      {pb && pb.events && <WeekendContext result={pb} />}

      {history.length > 0 && (
        <div data-testid="quick-check-history" className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Dernières vérifications</p>
          <ul className="flex flex-wrap gap-2">
            {history.map((h, i) => {
              const t = VERDICTS[h.verdict]?.tone || "amber";
              const dot = t === "green" ? "bg-emerald-500" : t === "amber" ? "bg-amber-500" : "bg-rose-500";
              return (
                <li key={i} data-testid={`quick-check-history-item-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                  <span className={`h-2 w-2 rounded-full ${dot}`} />
                  {h.date.slice(8, 10)}/{h.date.slice(5, 7)} — {h.city}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
